// src/tabs/AwarenessAttentionTab.jsx
// Awareness & Attention · Mindfulness-native landing page (Version 1, colorful tiles)

import React, { useState } from "react";

/* ------------------------------------------------------------------ */
/* Data: grouped “doorways” with gentle color washes                  */
/* ------------------------------------------------------------------ */

const FOUNDATIONS = [
  {
    name: "Present-moment awareness",
    desc: "Being where your feet are, right now.",
    invite:
      "If you’d like, feel one breath all the way in and all the way out.",
    color: "#3B82F6", // blue
  },
  {
    name: "Noticing without judging",
    desc: "Letting experience be here without fixing or labeling it.",
    invite:
      "For a few moments, simply note: ‘seeing’, ‘hearing’, ‘thinking’, ‘feeling’.",
    color: "#60A5FA", // lighter blue
  },
  {
    name: "Attention as an act of love",
    desc: "What you attend to, you nourish.",
    invite:
      "You might gently offer full attention to one part of your body, as if you were listening to a friend.",
    color: "#F97316", // warm orange
  },
  {
    name: "The wandering mind",
    desc: "You’re not doing it wrong — this is how the mind moves.",
    invite:
      "When you notice you’ve drifted, you could simply say ‘thinking’ and return to the next breath.",
    color: "#FACC15", // soft yellow
  },
];

/* New middle layer: Seeing → Softening → Letting Be → Returning */
const LETTING_GO = [
  {
    name: "Seeing the grip",
    desc: "Recognizing when the mind is holding tight.",
    invite:
      "You might notice, ‘Oh, I’m clinging to this thought/feeling right now,’ without trying to change it yet.",
    color: "#F97316", // gentle warm orange
  },
  {
    name: "Softening around experience",
    desc: "Letting go begins with gentleness, not force.",
    invite:
      "You could imagine softening the edges around the experience, as if giving it a little more room to breathe.",
    color: "#22C55E", // green
  },
  {
    name: "Letting be, not pushing away",
    desc: "Allowing thoughts and feelings to rise and fall.",
    invite:
      "For a few breaths, see what it’s like to let the experience be exactly as it is, without fixing or suppressing.",
    color: "#6366F1", // indigo
  },
  {
    name: "Returning with kindness",
    desc: "Coming back without scolding yourself.",
    invite:
      "When you notice you’ve drifted, you might quietly say, ‘It’s okay,’ and gently return to the breath or the body.",
    color: "#EC4899", // pink
  },
];

const MIND_MOVES = [
  {
    name: "The observer self",
    desc: "You are not your thoughts; you are the awareness that notices them.",
    invite:
      "When a strong thought appears, add the phrase ‘I notice the thought that…’ in front of it.",
    color: "#A855F7", // purple
  },
  {
    name: "Focused & open awareness",
    desc: "Sometimes we zoom in; sometimes we rest wide.",
    invite:
      "You might feel three breaths at the nose, then let awareness widen to include sounds and body.",
    color: "#22C55E", // green
  },
  {
    name: "Attentional shifts",
    desc: "Seeing the tiny jumps of mind from one thing to another.",
    invite:
      "Next time you switch tasks, pause for one breath and silently name it: ‘shifting’.",
    color: "#0EA5E9", // cyan
  },
  {
    name: "Meta-awareness",
    desc: "Noticing that you are noticing.",
    invite:
      "At some point today, you might quietly note: ‘aware of being aware’.",
    color: "#EC4899", // pink
  },
  {
    name: "Distraction awareness",
    desc: "Seeing what pulls you — and why.",
    invite:
      "When you reach for your phone, you could ask, ‘What am I moving away from right now?’.",
    color: "#F97316", // orange
  },
];

const RELATING = [
  {
    name: "Reactive vs. responsive awareness",
    desc: "Sensing the space between stimulus and response.",
    invite:
      "When you feel triggered, you might count three slow breaths before acting, speaking, or typing.",
    color: "#F97316",
  },
  {
    name: "Curiosity",
    desc: "Meeting this moment with beginner’s mind.",
    invite:
      "You could ask, ‘What is this like?’ about whatever you’re feeling right now, as if for the first time.",
    color: "#22C55E",
  },
  {
    name: "Attention in relationship",
    desc: "Offering presence to another person.",
    invite:
      "In your next conversation, see if you can listen all the way to the end of a sentence before planning your reply.",
    color: "#38BDF8",
  },
  {
    name: "Inner & outer attention",
    desc: "Balancing what’s happening inside and around you.",
    invite:
      "For a minute, you might alternate noticing an inner sensation and then an outer sound.",
    color: "#A855F7",
  },
];

const MODERN_LIFE = [
  {
    name: "Body as anchor",
    desc: "The body is always in the now.",
    invite:
      "You might feel your feet on the ground or your seat on the chair for ten slow breaths.",
    color: "#22C55E",
  },
  {
    name: "Embodied attention",
    desc: "Living from the whole body, not just the head.",
    invite:
      "You could slowly scan from feet to head, noticing warmth, tension, or ease along the way.",
    color: "#14B8A6",
  },
  {
    name: "Mood-dependent attention",
    desc: "How inner weather colors what you see.",
    invite:
      "If you like, name your mood like a forecast: ‘overcast’, ‘sunny breaks’, ‘heavy rain’, ‘clear but cold’.",
    color: "#FACC15",
  },
  {
    name: "Digital attention & fragmentation",
    desc: "Seeing how screens slice awareness into tiny pieces.",
    invite:
      "You might choose one app and open it only once in the next hour, noticing urges in between.",
    color: "#6366F1",
  },
  {
    name: "Attentional fatigue & recovery",
    desc: "Even attention gets tired and needs rest.",
    invite:
      "Perhaps take a brief look out a window, or close your eyes for three breaths, before you continue.",
    color: "#F97316",
  },
];

/* ------------------------------------------------------------------ */
/* Component                                                           */
/* ------------------------------------------------------------------ */

const ATTENTION_STATES = [
  "Drifting",
  "Scattered",
  "Quiet",
  "Curious",
  "Heavy",
  "Energized",
  "I’m not sure",
];

const STATE_REFLECTION = {
  Drifting:
    "Drifting is completely natural. Noticing that you’re drifting is already awareness.",
  Scattered:
    "Scattered attention often means you’ve been holding a lot. You might let just one thing matter for this breath.",
  Quiet:
    "If things feel quiet, you might gently rest in that calm without needing to hold onto it.",
  Curious:
    "Curiosity is a beautiful doorway. You could ask, ‘What is this moment like, from the inside?’",
  Heavy:
    "If attention feels heavy, that’s okay. You might simply feel the weight of your body being supported.",
  Energized:
    "If you’re energized, notice where that energy lives in the body. Does it tingle, buzz, vibrate?",
  "I’m not sure":
    "Not knowing is welcome here. You might just notice, ‘This is what not knowing feels like.’",
};

export default function AwarenessAttentionTab({ onOpenMeta }) {
  const [state, setState] = useState("");

  const heroImg =
    "https://images.unsplash.com/photo-1500530855697-b586d89ba3ee?q=80&w=1600&auto=format&fit=crop";

  return (
    <>
      {/* HERO — soft arrival, no urgency */}
      <section
        className="card lg section"
        style={{ padding: 0, overflow: "hidden" }}
        aria-label="Awareness and attention"
      >
        <div style={{ position: "relative" }}>
          {/* eslint-disable-next-line */}
          <img
            src={heroImg}
            alt=""
            style={{ width: "100%", height: 220, objectFit: "cover" }}
          />
          <div
            style={{
              position: "absolute",
              inset: 0,
              background:
                "linear-gradient(180deg, rgba(15,23,42,.35), rgba(15,23,42,.85))",
            }}
          />
          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "flex-end",
              padding: 24,
              color: "#fff",
            }}
          >
            <div>
              <div className="chips" style={{ marginBottom: 8 }}>
                <span
                  className="pill"
                  style={{
                    background: "rgba(255,255,255,.12)",
                    color: "#fff",
                    borderColor: "rgba(255,255,255,.4)",
                  }}
                >
                  Domains of Awareness
                </span>
                <span
                  className="pill"
                  style={{
                    background: "rgba(255,255,255,.12)",
                    color: "#fff",
                    borderColor: "rgba(255,255,255,.4)",
                  }}
                >
                  Awareness & Attention
                </span>
              </div>
              <h1 className="h1" style={{ margin: 0, color: "#fff" }}>
                A place to arrive, slowly.
              </h1>
              <div className="sm" style={{ opacity: 0.96, marginTop: 6 }}>
                Before you learn anything about attention, you’re invited to
                feel it — right here, as your life is unfolding.
              </div>
              <div
                style={{
                  marginTop: 10,
                  display: "flex",
                  gap: 8,
                  flexWrap: "wrap",
                }}
              >
                <button
                  className="btn secondary"
                  onClick={() =>
                    alert(
                      "If you like, close your eyes for three slow breaths.\n\nFeel the inhale, the exhale, and everything in between."
                    )
                  }
                >
                  Take a breath (gentle prompt)
                </button>
                <span className="xs" style={{ opacity: 0.9 }}>
                  “Mindfulness means paying attention in a particular way: on
                  purpose, in the present moment, and non-judgmentally.”
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MINI SELF-CHECK — reflects, doesn’t judge */}
      <section className="card lg section">
        <div className="section-head">
          <h2 className="h2">How is your attention today?</h2>
          <span className="xs muted">
            There’s no right answer. Simply noticing is the practice.
          </span>
        </div>
        <div
          className="chips"
          style={{ flexWrap: "wrap", gap: 8, marginBottom: 10 }}
        >
          {ATTENTION_STATES.map((label) => (
            <button
              key={label}
              className="pill"
              onClick={() => setState(label)}
              style={{
                borderColor:
                  state === label ? "var(--brand)" : "rgba(148,163,184,.4)",
                background:
                  state === label ? "var(--brand-10)" : "rgba(248,250,252,1)",
                color: state === label ? "var(--brand-600)" : "var(--muted)",
                fontWeight: state === label ? 700 : 500,
              }}
            >
              {label}
            </button>
          ))}
        </div>
        <p className="sm" style={{ margin: 0 }}>
          {state
            ? STATE_REFLECTION[state]
            : "If you’re not sure yet, you can simply keep reading and notice what you notice."}
        </p>
      </section>

      {/* SHORT INVITATION PRACTICE */}
      <section className="card lg section">
        <div className="section-head">
          <h2 className="h2">A simple invitation</h2>
          <span className="xs muted">
            You can pause here for 30–60 seconds, if that feels okay.
          </span>
        </div>
        <ol className="sm" style={{ paddingLeft: 18, margin: 0 }}>
          <li>
            Let your body be supported by whatever you’re sitting or standing
            on.
          </li>
          <li>
            Feel one full breath from beginning to end — the in-breath, the
            out-breath.
          </li>
          <li>
            If the mind wanders (as it will), gently notice, “thinking,” and
            invite attention back, as if returning to a friend.
          </li>
          <li>
            For these few breaths, let this be enough. No fixing, no improving,
            just being here.
          </li>
        </ol>
      </section>

      {/* PAUSE CARD — built-in “non-doing” */}
      <section className="card lg section" aria-label="Pause">
        <div
          style={{
            textAlign: "center",
            padding: "12px 8px",
          }}
        >
          <div className="xs muted" style={{ marginBottom: 4 }}>
            You’re welcome to pause here.
          </div>
          <div className="sm">
            Maybe soften your gaze or look away from the screen for one breath
            before you continue.
          </div>
        </div>
      </section>

      {/* GROUP 1 — Foundations */}
      <SectionGroup
        title="Foundations of seeing clearly"
        subtitle="Places to begin, or begin again."
        items={FOUNDATIONS}
      />

      {/* NEW MIDDLE LAYER — Letting Go & Letting Be */}
      <SpacerLine />
      <SectionGroup
        title="Letting Go & Letting Be"
        subtitle="Relating wisely to thoughts, emotions, and urges."
        items={LETTING_GO}
      />

      {/* GROUP 2 — How the mind moves */}
      <SpacerLine />
      <SectionGroup
        title="How the mind moves"
        subtitle="Doorways into drifting, returning, and noticing."
        items={MIND_MOVES}
        onOpenMeta={onOpenMeta}
      />

      <SpacerLine />

      {/* GROUP 3 — How we relate */}
      <SectionGroup
        title="How we relate"
        subtitle="Attention in the space between you and the world."
        items={RELATING}
      />

      <SpacerLine />

      {/* GROUP 4 — Attention in modern life */}
      <SectionGroup
        title="Attention in modern life"
        subtitle="Screens, moods, bodies, and the need to rest."
        items={MODERN_LIFE}
      />

      {/* CLOSING — permission, not homework */}
      <section className="card lg section">
        <p className="sm" style={{ marginTop: 0 }}>
          Before you leave this page, you might notice one thing you’re aware of
          now that you weren’t when you arrived. It could be a sensation, a
          mood, a thought, or the simple fact that you are breathing.
        </p>
        <p className="xs muted" style={{ marginBottom: 0 }}>
          Let that awareness be enough. You can always begin again with the next
          breath.
        </p>
      </section>
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Small helper components                                            */
/* ------------------------------------------------------------------ */

function SectionGroup({ title, subtitle, items, onOpenMeta }) {
  return (
    <section className="card lg section">
      <div className="section-head">
        <h2 className="h2">{title}</h2>
        <span className="xs muted">{subtitle}</span>
      </div>
      <div
        className="grid"
        style={{
          gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
          gap: 12,
        }}
      >
        {items.map((b) => {
          const color = b.color || "#2563EB";
          const bg = `linear-gradient(135deg, ${color}12, #ffffff)`;
          const isMeta =
            b.name === "Meta-awareness" && typeof onOpenMeta === "function";
          const Wrapper = isMeta ? "button" : "div";

          return (
            <Wrapper
              key={b.name}
              type={isMeta ? "button" : undefined}
              className="card"
              onClick={isMeta ? onOpenMeta : undefined}
              style={{
                borderRadius: 18,
                background: bg,
                border: "1px solid var(--line)",
                boxShadow:
                  "0 1px 2px rgba(15,23,42,.04), 0 10px 24px rgba(15,23,42,.06)",
                padding: 16,
                textAlign: "left",
                cursor: isMeta ? "pointer" : "default",
              }}
            >
              <h3
                className="h2"
                style={{ marginTop: 0, fontSize: 15, marginBottom: 4 }}
              >
                {b.name}
              </h3>
              <p className="xs muted" style={{ marginTop: 0, marginBottom: 8 }}>
                {b.desc}
              </p>
              <p className="xs" style={{ margin: 0 }}>
                {b.invite}
              </p>
            </Wrapper>
          );
        })}
      </div>
    </section>
  );
}

function SpacerLine() {
  return (
    <section className="section" aria-hidden="true">
      <div
        style={{
          height: 1,
          background:
            "linear-gradient(to right, transparent, #E5E7EB, transparent)",
          margin: "4px 0 8px",
        }}
      />
    </section>
  );
}
